/*******************************************************************************
* Licensed Materials - Property of HCL
* (c) Copyright HCL Technologies Ltd. 2020, 2023. All Rights Reserved.

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/

import { Text } from "vr-react-components";
import React from "react";
import { BackgroundPanel } from "vr-react-components";
import { useSelector, useDispatch } from "react-redux";
import { Button } from "vr-react-components";
import { resetRotationCenterGraph } from "../../store/slice/centerGraphSlice";
import { handleGoToExploreRoom } from "../../handlers/ExploreRoomHandlers";

const BarWidgetTooltip = ({ stateName, width, height, children, ...otherAttributes }) => {
    const data = useSelector((state) => state[stateName].data);
    const centerGraphState = useSelector((state) => state[stateName].position);
    const canModify = useSelector((state) => state.private.device.canModify);

    const title = data.title;
    const axis = data.axis;
    const isGraphDataAvailable =
        data.values &&
        data.values.length &&
        data.xLabels &&
        data.xLabels.length;

    const dispatch = useDispatch();

    const handleResetDisplay = () => {
        dispatch(resetRotationCenterGraph())
    }

    const exploreRoomButton = () => {
        let range = {
            from: data.xLabels[0],
            to: data.xLabels[data.xLabels.length - 1]
        }

        const checkedWorkstation = data.zLabels.map(() => true)

        handleGoToExploreRoom(
            data,
            checkedWorkstation,
            range,
        )
    }

    if (!title) return null;

    if (data.noData)
        return (
            <BackgroundPanel width={width} height={height} {...otherAttributes}>
                <Text
                    width={width}
                    sizeDecimal={12 / height}
                    text="No data available"
                    align="center"
                />
            </BackgroundPanel>
        );

    return (
        <BackgroundPanel width={width} height={height} {...otherAttributes} opacity={1}>
            {children}
            <a-entity position={`${-width / 2 + width / 40} 0 0.001`}>
                <Text
                    text={`${title}`}
                    width={width * 0.325}
                    sizeDecimal={12 / height}
                    align="left"
                />
                <a-plane
                    position={`${width * 0.35} 0 0`}
                    width={width / 200}
                    height={height * 0.8}
                    color="#c6c6c6"
                />
                <Button
                    text="Explore"
                    type={Button.constants.primary}
                    position={`${width - width / 20 - width * 0.1} ${height * 0.2} ${height / 100
                        }`}
                    aframeClasses={canModify ? "collidable" : ""}
                    width={width * 0.2}
                    height={height / 4}
                    textSizeDecimal={12 / height}
                    onClickHandler={exploreRoomButton}
                    disabled={!isGraphDataAvailable}
                />
                <Button
                    text="Reset display"
                    disabled={
                        centerGraphState.rotation.x === 0 &&
                        centerGraphState.rotation.y === 0 &&
                        centerGraphState.rotation.z === 0 &&
                        centerGraphState.scale === 1
                    }
                    type={Button.constants.primary}
                    aframeClasses={canModify ? "collidable" : ""}
                    position={`${width - width / 20 - width * 0.1} ${-height * 0.2} ${height / 100
                        }`}
                    width={width * 0.2}
                    height={height / 4}
                    textSizeDecimal={12 / height}
                    onClickHandler={handleResetDisplay}
                />
                <a-entity position={`${width * 0.4} ${height * 0.2} 0`}>
                    <a-box
                        color="red"
                        depth={height / 100}
                        width={height * 0.1}
                        height={height * 0.1}
                    />
                    <Text
                        text={`Axis X:`}
                        width={width / 3}
                        sizeDecimal={12 / height}
                        align="left"
                        position={`${height * 0.1} 0 0`}
                        secondary
                    />
                    <Text
                        text={axis.x}
                        width={width / 3}
                        sizeDecimal={12 / height}
                        align="left"
                        position={`${width * 0.125} 0 0`}
                    />
                    <a-box
                        color="green"
                        depth={height / 100}
                        width={height * 0.1}
                        height={height * 0.1}
                        position={`0 ${-height * 0.2} 0`}
                    />
                    <Text
                        text={`Axis Y:`}
                        position={`${height * 0.1} ${-height * 0.2} 0`}
                        width={width / 3}
                        sizeDecimal={12 / height}
                        align="left"
                        secondary
                    />
                    <Text
                        text={axis.y}
                        position={`${width * 0.125} ${-height * 0.2} 0`}
                        width={width / 3}
                        sizeDecimal={12 / height}
                        align="left"
                    />
                    <a-box
                        color="blue"
                        depth={height / 100}
                        width={height * 0.1}
                        height={height * 0.1}
                        position={`0 ${-height * 0.4} 0`}
                    />
                    <Text
                        text={`Axis Z:`}
                        position={`${height * 0.1} ${-height * 0.4} 0`}
                        width={width / 3}
                        sizeDecimal={12 / height}
                        align="left"
                        secondary
                    />
                    <Text
                        text={axis.z}
                        position={`${width * 0.125} ${-height * 0.4} 0`}
                        width={width / 3}
                        sizeDecimal={12 / height}
                        align="left"
                    />
                </a-entity>
            </a-entity>
        </BackgroundPanel>
    );
};

export default BarWidgetTooltip;
